import { USE_MOCKS, api, mockDelay } from "@/lib/api"

/**
 * Las sociedades del tenant activo.
 *
 * Salen de `GET /masters/entities`, que devuelve el catalogo con lo que hay
 * cargado en cada una (libros, documentos, periodos abiertos). Ese conteo no es
 * decoracion: mirar la sociedad equivocada es un error caro y silencioso, y es
 * la senal mas barata para notarlo antes de asentar nada.
 *
 * Sin sociedad elegida, una pantalla que la necesita lo DICE en vez de mandar la
 * peticion y traducir el 422: el 422 nombra un campo que el usuario no puede
 * rellenar y llega despues de teclear el formulario entero. Y en los listados
 * `entity_id` es opcional, asi que sin sociedad no falla — mezcla las filas de
 * todas y muestra un total que no es de nadie, que es peor que un error.
 */

export interface Entity {
  id: string
  /** Razon social. Vacio cuando la entidad se conoce solo por su id (fuente 2). */
  name: string
  /** Identificador fiscal (RUC / RUT / CNPJ). Se muestra con `.code`. */
  taxId: string | null
  /** ISO-3166 alpha-2. Gobierna idioma y formato de la pantalla. */
  country: string | null
  /**
   * `true` cuando solo se conoce el id: viene de un alcance del perfil y no de
   * la bandeja. La pantalla lo dice en vez de fingir que sabe el nombre.
   */
  parcial?: boolean
  /** Con que esta poblada. Ausentes cuando la entidad es `parcial`. */
  libros?: number
  documentos?: number
  periodosAbiertos?: number
}

/** Fila de `GET /masters/entities`, en el vocabulario del backend. */
interface EntityRow {
  id: string
  razon_social: string
  tax_id: string | null
  pais: string | null
  moneda_funcional?: string | null
  regimen_tributario?: string | null
  /** Con que esta poblada. Es la senal barata para notar que estas en la
   *  sociedad equivocada ANTES de asentar nada. */
  libros?: number
  documentos?: number
  periodos_abiertos?: number
}

/** Fila de `GET /filings/inbox`. Se conserva solo para `entitiesFromInbox`,
 *  que sigue siendo util cuando el catalogo no esta disponible. */
interface BandejaRow {
  entity_id: string
  razon_social: string | null
  tax_id: string | null
  pais: string | null
  tenant_id?: string | null
}

const MOCK_ENTITIES: Record<string, Entity[]> = {
  // Un tenant con varias sociedades (grupo economico) y dos con una sola:
  // asi el modo mock ejercita tambien la autoseleccion.
  "t-001": [
    { id: "e-001", name: "Andina Servicios SAC", taxId: "20512223334", country: "PE" },
    { id: "e-002", name: "Andina Logistica SAC", taxId: "20512223335", country: "PE" },
  ],
  "t-002": [{ id: "e-003", name: "Nexo Digital SpA", taxId: "76.123.456-7", country: "CL" }],
  "t-003": [{ id: "e-004", name: "Corporacao Vela Ltda", taxId: "12.345.678/0001-90", country: "BR" }],
}

/** Nombre para una entidad de la que solo se sabe el id. */
export function nombreProvisional(entityId: string) {
  return `Sociedad ${entityId.slice(0, 8)}`
}

/**
 * Une las dos fuentes. La bandeja manda: si una entidad aparece en las dos, se
 * queda la que tiene nombre. Orden alfabetico, estable entre recargas.
 */
export function mergeEntities(deLaBandeja: Entity[], idsDelAlcance: string[]): Entity[] {
  const porId = new Map<string, Entity>()
  for (const entidad of deLaBandeja) porId.set(entidad.id, entidad)
  for (const id of idsDelAlcance) {
    if (porId.has(id)) continue
    porId.set(id, { id, name: "", taxId: null, country: null, parcial: true })
  }
  return [...porId.values()].sort((a, b) =>
    (a.name || nombreProvisional(a.id)).localeCompare(b.name || nombreProvisional(b.id)),
  )
}

export function entitiesFromInbox(rows: BandejaRow[], tenantId: string): Entity[] {
  const porId = new Map<string, Entity>()
  for (const row of rows ?? []) {
    if (!row?.entity_id) continue
    // La bandeja es cross-tenant a proposito (el contador tiene cartera): sin
    // este filtro, cambiar de cliente podria dejar en la lista sociedades de
    // otro. El header ya la acota, pero no se depende de eso.
    if (row.tenant_id && row.tenant_id !== tenantId) continue
    if (porId.has(row.entity_id)) continue
    porId.set(row.entity_id, {
      id: row.entity_id,
      name: row.razon_social ?? "",
      taxId: row.tax_id ?? null,
      country: row.pais ?? null,
    })
  }
  return [...porId.values()]
}

/**
 * Sociedades visibles del tenant activo.
 *
 * `tenantId` se manda explicito y no se deja al estado del cliente HTTP: esta
 * llamada se dispara justo cuando el tenant cambia, y una carrera ahi listaria
 * las sociedades del cliente anterior.
 */
export function entitiesFromApi(rows: EntityRow[]): Entity[] {
  return (rows ?? [])
    .filter((r) => r?.id)
    .map((r) => ({
      id: r.id,
      name: r.razon_social ?? "",
      taxId: r.tax_id ?? null,
      country: r.pais ?? null,
      libros: r.libros,
      documentos: r.documentos,
      periodosAbiertos: r.periodos_abiertos,
    }))
}

export async function fetchEntities(
  tenantId: string | null,
  idsDelAlcance: string[] = [],
): Promise<Entity[]> {
  if (!tenantId) return []
  if (USE_MOCKS) return mockDelay(MOCK_ENTITIES[tenantId] ?? [])

  const rows = await api.get<EntityRow[]>("/masters/entities", { tenantId })
  // `idsDelAlcance` sigue entrando en la mezcla: el catalogo es la fuente de
  // verdad, pero si un alcance nombra una sociedad que el listado no devolvio
  // —permisos, o una carrera al cambiar de cliente— es mejor mostrarla marcada
  // como parcial que hacerla desaparecer sin decir nada.
  return mergeEntities(entitiesFromApi(rows), idsDelAlcance)
}

/**
 * El pais que gobierna idioma, formato y terminologia de la pantalla.
 *
 * SIGUE A LA SOCIEDAD, NO AL CLIENTE. Es la correccion de un supuesto que era
 * cierto cuando cada tenant tenia una sola sociedad y dejo de serlo el dia que
 * aparecieron cuatro: Siete opera en Peru, Mexico, Brasil y Panama bajo el mismo
 * tenant, y el tenant es peruano. Con el criterio anterior, abrir la sociedad de
 * Sao Paulo mostraba la pantalla en español con soles y la palabra RUC — sobre
 * datos de una empresa brasileña que declara en portugues, en reales y con CNPJ.
 *
 * El orden es: la sociedad activa, si se sabe cual es y de que pais; despues el
 * pais del cliente; y si no hay nada, `undefined`, que `I18nProvider` resuelve
 * como PE. Los dos escalones intermedios importan: mientras el catalogo de
 * sociedades viaja, `activeEntityId` es null y no saber cual es la sociedad no
 * es saber que no hay ninguna. Devolver `undefined` ahi haria que la pantalla
 * cambiara de idioma sola un segundo despues de cargar.
 */
export function paisDeLaPantalla(
  entities: Entity[],
  activeEntityId: string | null,
  paisDelCliente?: string | null,
): string | undefined {
  const sociedad = activeEntityId
    ? entities.find((e) => e.id === activeEntityId)
    : undefined
  return sociedad?.country ?? paisDelCliente ?? undefined
}
